import React from 'react';

const formatPercent = (score) => (
  typeof score === 'number' ? `${Math.round(score * 100)}%` : 'N/A'
);

const renderSource = (label, source, confidence) => (
  <div className="ocr-merge-source">
    <dt>{label}</dt>
    <dd>
      {source ? (
        <>
          <strong>{source.detectedName || 'Name not detected'}</strong>
          {confidence && (
            <span>
              {confidence.type.toUpperCase()} {formatPercent(confidence.score)}
            </span>
          )}
        </>
      ) : (
        <span>Not matched</span>
      )}
    </dd>
  </div>
);

const UnresolvedGroup = ({ title, records }) => {
  if (!records.length) return null;

  return (
    <section className="ocr-merge-unresolved-group">
      <h5>{title}</h5>
      <div className="ocr-merge-unresolved-list">
        {records.map((record, index) => (
          <article className="ocr-merge-unresolved-card" key={`${title}-${record.sourceRowId || index}`}>
            <strong>{record.detectedName || 'Name not detected'}</strong>
            <div className="ocr-row-debug-meta">
              {record.damage != null && <span>Damage: {record.damage}</span>}
              {record.captureKills != null && <span>Capture Kills: {record.captureKills}</span>}
              {record.operatorValue != null && <span>Operator Value: {record.operatorValue}</span>}
              {record.sourceScreenshotName && <span>{record.sourceScreenshotName}</span>}
            </div>
            {record.candidates?.length > 0 && (
              <div className="ocr-merge-candidates">
                {record.candidates.map((candidate) => (
                  <span key={`${candidate.playerIndex}-${candidate.detectedName}`}>
                    {candidate.detectedName || 'Unknown'} - {formatPercent(candidate.score)}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

const ControlMergeDebug = ({ result }) => {
  if (!result) return null;

  return (
    <div className="ocr-match-parse-panel ocr-merge-parse-panel">
      <div className="ocr-results-heading">
        <h4>Control Merged OCR Result</h4>
        <p>Preview/debug data only. This does not modify the Player Stats form.</p>
      </div>

      <div className="ocr-row-debug-meta">
        <span>Match Players: {result.stats.matchPlayers}</span>
        <span>Impact Records: {result.stats.impactRecords}</span>
        <span>Prop Records: {result.stats.propRecords}</span>
        <span>Exact Matches: {result.stats.exactMatches}</span>
        <span>Fuzzy Matches: {result.stats.fuzzyMatches}</span>
        <span>Ambiguous: {result.stats.ambiguousMatches}</span>
        <span>Unmatched: {result.stats.unmatchedRecords}</span>
      </div>

      {result.warnings.map((warning) => (
        <p className="ocr-row-warning" key={warning}>{warning}</p>
      ))}

      <div className="ocr-match-records">
        {result.players.map((player, index) => (
          <article className="ocr-match-record ocr-merge-record" key={`${player.detectedName}-${index}`}>
            <header>
              <strong>Player {index + 1}</strong>
              <span>{player.detectedName || 'Name not detected'}</span>
            </header>

            <dl className="ocr-merge-stats">
              <div>
                <dt>Score</dt>
                <dd>{player.score ?? 'N/A'}</dd>
              </div>
              <div>
                <dt>K/D/A</dt>
                <dd>{`${player.kills ?? 'N/A'}/${player.deaths ?? 'N/A'}/${player.assists ?? 'N/A'}`}</dd>
              </div>
              <div>
                <dt>Impact</dt>
                <dd>{player.impact ?? 'N/A'}</dd>
              </div>
              <div>
                <dt>Damage</dt>
                <dd>{player.damage ?? 'N/A'}</dd>
              </div>
              <div>
                <dt>Capture Kills</dt>
                <dd>{player.captureKills ?? 'N/A'}</dd>
              </div>
              <div>
                <dt>Operator Value</dt>
                <dd>{player.operatorValue ?? 'N/A'}</dd>
              </div>
            </dl>

            <dl className="ocr-merge-sources">
              {renderSource('Match', player._sources.matchDetails, { type: 'exact', score: 1 })}
              {renderSource('Impact', player._sources.impactDetails, player._matchConfidence.impact)}
              <div className="ocr-merge-source">
                <dt>Prop</dt>
                <dd>
                  {player._sources.propDetails.length ? (
                    player._sources.propDetails.map((source, sourceIndex) => (
                      <span key={`${source.sourceScreenshotIndex}-${source.sourceRowId}-${sourceIndex}`}>
                        {source.detectedName || 'Name not detected'} - {source.matchType.toUpperCase()} {formatPercent(source.matchScore)}
                      </span>
                    ))
                  ) : (
                    <span>Not matched</span>
                  )}
                </dd>
              </div>
            </dl>

            {player._propValueCandidates?.length > 0 && (
              <div className="ocr-merge-candidates">
                {player._propValueCandidates.map((value) => (
                  <span key={value}>Prop candidate: {value}</span>
                ))}
              </div>
            )}

            {player._warnings.length > 0 && (
              <div className="ocr-field-warnings">
                {player._warnings.map((warning) => (
                  <p key={warning}>{warning}</p>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>

      <UnresolvedGroup title="Unmatched Impact" records={result.unmatched.impact} />
      <UnresolvedGroup title="Unmatched Prop" records={result.unmatched.prop} />
      <UnresolvedGroup title="Ambiguous Impact" records={result.ambiguous.impact} />
      <UnresolvedGroup title="Ambiguous Prop" records={result.ambiguous.prop} />
    </div>
  );
};

export default ControlMergeDebug;
